import React from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { dateTriggerClass } from "../../lib/field-frame.js";
import { useFormField } from "../../lib/form-field-context.js";
import {
  getLayerRoot,
  useDismissable,
  useLayer,
  usePosition,
} from "../../primitives/index.js";
import { useOverlayPresence } from "../../primitives/OverlaySurface.js";
import { FieldClearButton } from "../../atoms/Button/FieldClearButton.js";

/* ---------------- DatePicker ----------------
   Engine-backed (strategy §14.4 increment 5.2): the calendar portals into the
   layer root at z-popover (raw z-50 retired), placement flips via usePosition,
   and Esc/outside-press go through useDismissable (Esc refocuses the trigger,
   and so does an outside press that leaves focus nowhere).
   Grid arrow-key handling lives on the day buttons themselves (interactive
   elements), not on the dialog wrapper.

   ADR-0091: the calendar is an anchored overlay, so it LEAVES through
   `useOverlayPresence` rather than being destroyed in a frame — mounted for
   its exit, inert and unannounced while that exit plays. `usePosition` is fed
   `present`, not `open`, because a position that stops being computed on the
   closing frame resolves to null and throws the calendar at the top-left
   corner on its way out. Everything logical — the layer, dismissal, the
   month view, committing a day — keeps following `open`. */

export interface DatePickerProps {
  label?: string;
  hint?: string;
  /** Replaces the hint with a red message, marks the field invalid and links
   *  it via `aria-describedby` — the same contract `Input` accepts. */
  error?: string;
  /** ISO date string (YYYY-MM-DD). */
  value?: string;
  defaultValue?: string;
  onChange?: (iso: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** `sm` is the 28px row, `md` the 36px row. */
  size?: "sm" | "md";
  /** A ghost iconOnly Button size="xs" beside the trigger — never nested
   *  inside it — shown only when a date is set and the picker is enabled.
   *  Pressing it emits `onChange("")` and refocuses the trigger. */
  clearable?: boolean;
  /** Accessible name of the clear control. Default: "Clear date". */
  clearLabel?: string;
  id?: string;
  className?: string;
  name?: string;
  /** Earliest selectable date, inclusive (YYYY-MM-DD). Omit for no lower bound. */
  min?: string;
  /** Latest selectable date, inclusive (YYYY-MM-DD). Omit for no upper bound. */
  max?: string;
  /**
   * Names the trigger when there is no visible `label` — the same prop
   * `Select` already forwards.
   */
  "aria-label"?: string | undefined;
  /**
   * A description held OUTSIDE this control — the same prop `Input` and
   * `Select` already forward. Joined with the enclosing FormField's own ids
   * (and this component's own `error`, if set) rather than replacing them.
   */
  "aria-describedby"?: string | undefined;
  /**
   * An invalid state the CALLER holds, as `Input`/`Select` accept it — but it
   * paints the border ONLY. `aria-invalid` is not among the states ARIA
   * allows on `role="button"` (Select precedent, which `FormField` reaches
   * the same conclusion about for the error it owns); the field's own
   * `error` message, or the caller's own `aria-describedby`, is what carries
   * the reason to a reader.
   */
  "aria-invalid"?: boolean | "true" | "false" | undefined;
  /**
   * PB-4923 AC5 — bound to the calendar's OPEN lifetime, not to DOM focus.
   *
   * A caller leasing "somebody is choosing this date" cannot use the trigger's
   * focus events. The calendar is a popover: once it opens, focus moves onto a
   * day button inside it, so the trigger blurs while the person is still very
   * much editing — and the outside-press path closes through `useDismissable`
   * without the trigger ever blurring at all. So `onFocus` fires when the
   * calendar opens and `onBlur` when it closes, by ANY route (a committed pick,
   * Enter, Escape, an outside press, or toggling the trigger shut).
   *
   * That window is also the right one for a draft: arrow keys re-commit on
   * every press WITHOUT closing, so the claim is held across the whole
   * deliberation rather than dropped between keystrokes. Both are optional and
   * change nothing when absent.
   */
  onFocus?: () => void;
  onBlur?: () => void;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* The two month arrows speak the shared interaction grades (ADR-0092) instead
   of hand-spelling a hover and a press — and gain the focus ring they never
   had, which is what a keyboard user needs to see where paging happens. */
const MONTH_NAV_CLASS =
  "h-6 w-6 inline-flex items-center justify-center rounded text-pb-text-muted hover-quiet press focus-ring";

function parseISO(s: string | undefined): Date | undefined {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? undefined : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatPretty(d: Date): string {
  return `${(MONTHS[d.getMonth()] ?? "").slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/* Bounds are compared as YYYY-MM-DD strings, which sort lexicographically in
   calendar order for that fixed-width form. Comparing Date objects would drag
   the time of day in and make "the max day itself" unselectable. A malformed
   bound is ignored rather than silently locking the whole calendar. */
function outOfRange(d: Date, min?: string, max?: string): boolean {
  const iso = toISO(d);
  // `parseISO` returns a Date for a shape-valid but calendar-invalid bound
  // like `2026-02-30`, because the constructor rolls it forward — so the guard
  // passed and the bound then compared as the STRING "2026-02-30", which sorts
  // after every real February day and silently blocked the 28th and 29th.
  // Round-tripping is what proves the bound names a day that exists.
  if (min && isRealDate(min) && iso < min) return true;
  if (max && isRealDate(max) && iso > max) return true;
  return false;
}

/** True when `iso` names a day that actually exists. */
function isRealDate(iso: string): boolean {
  const d = parseISO(iso);
  return d !== undefined && toISO(d) === iso;
}

function enabledDate(base: Date, min?: string, max?: string): Date {
  if (outOfRange(base, min, undefined)) return parseISO(min) ?? base;
  if (outOfRange(base, undefined, max)) return parseISO(max) ?? base;
  return base;
}

export function DatePicker({
  label,
  hint,
  error,
  value,
  defaultValue,
  onChange,
  placeholder = "Pick a date",
  disabled,
  size,
  clearable,
  clearLabel = "Clear date",
  id,
  className,
  name,
  min,
  max,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  onFocus,
  onBlur,
}: DatePickerProps) {
  const autoId = React.useId();
  const did = id ?? autoId;
  const field = useFormField();
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<string | undefined>(
    defaultValue,
  );
  const currentISO = isControlled ? value : internal;
  const currentDate = parseISO(currentISO);

  // Empty-string error means "no error" — don't flag the field invalid
  // (Input precedent).
  const hasError = !!error;
  const description = hasError ? `${did}-description` : undefined;
  const callerInvalid = ariaInvalid === true || ariaInvalid === "true";
  const invalid = hasError || callerInvalid || field?.invalid;
  // FormField clones the child with the very ids it also puts on the
  // context, so a caller's own description and the enclosing field's can
  // repeat; join through a set (Select precedent).
  const describedBy =
    [
      ...new Set(
        `${ariaDescribedBy ?? ""} ${description ?? ""} ${field?.describedBy ?? ""}`.split(
          " ",
        ),
      ),
    ]
      .filter(Boolean)
      .join(" ") || undefined;
  const showClear = clearable && !disabled && !!currentISO;

  const [open, setOpen] = React.useState(false);
  const layerId = useLayer({ open });

  /* PB-4923 AC5 — report the OPEN transition, derived from state rather than
     wired at each `setOpen`. There are five paths that close this calendar
     (a committed pick, Enter in the grid, Escape, an outside press, and
     toggling the trigger) and one that opens it twice over; hanging the
     callbacks off the state means none can be missed, and a sixth added later
     is covered for free. The `wasOpen` guard makes it edge-triggered, so the
     identity churn of an inline `onFocus` prop cannot re-fire a claim. */
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open === wasOpenRef.current) return;
    wasOpenRef.current = open;
    if (open) onFocus?.();
    else onBlur?.();
  }, [open, onFocus, onBlur]);
  const today = React.useMemo(() => new Date(), []);
  const [view, setView] = React.useState<{ year: number; month: number }>(
    () => {
      const base = currentDate ?? today;
      return { year: base.getFullYear(), month: base.getMonth() };
    },
  );

  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const calRef = React.useRef<HTMLDivElement>(null);
  const pendingFocusRef = React.useRef<string | null>(null);
  const refocusTimerRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(refocusTimerRef.current), []);

  const handleClear = () => {
    if (!isControlled) setInternal("");
    onChange?.("");
    triggerRef.current?.focus?.();
  };

  React.useEffect(() => {
    if (open) {
      // A refocus still waiting from the last outside press must not pull
      // focus out of the calendar that has just reopened.
      window.clearTimeout(refocusTimerRef.current);
      const base = enabledDate(currentDate ?? today, min, max);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: snap the calendar view to an enabled date when the picker opens (open-time only; currentDate is deliberately excluded from deps).
      setView({ year: base.getFullYear(), month: base.getMonth() });
    }
    // Snap the view to the selection at open time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { present, closing, motionProps } = useOverlayPresence(open, {
    ref: calRef,
  });

  const onDismiss = React.useCallback((reason: "escape" | "outside-press") => {
    setOpen(false);
    if (reason === "escape") {
      triggerRef.current?.focus?.();
      return;
    }
    /* The press closes the calendar before the browser moves focus. A press
       on blank space (a dialog's scrim) leaves focus on <body> or in the
       calendar now leaving, so it goes back to the trigger; a press on
       another field keeps focus there. */
    window.clearTimeout(refocusTimerRef.current);
    refocusTimerRef.current = window.setTimeout(() => {
      const active = document.activeElement;
      if (active === document.body || calRef.current?.contains(active)) {
        triggerRef.current?.focus?.();
      }
    }, 0);
  }, []);
  const insideRefs = React.useMemo(() => [wrapRef, calRef], []);
  useDismissable({ layerId, open, onDismiss, insideRefs });

  const pos = usePosition({
    // `present`, so the box keeps its place while it fades out.
    open: present,
    triggerRef,
    contentRef: calRef,
    align: "start",
    sideOffset: 4,
  });
  React.useLayoutEffect(() => {
    if (!open || !pos || pendingFocusRef.current === null) return;
    const requested = calRef.current?.querySelector<HTMLButtonElement>(
      `[data-calendar-date="${pendingFocusRef.current}"]:not([disabled])`,
    );
    if (!requested) return;
    pendingFocusRef.current = null;
    requested.focus();
  }, [open, pos, view]);

  const commit = (d: Date) => {
    // A calendar that is already leaving must not answer a click: CSS drops
    // its pointer events, but nothing stops a synthetic one.
    if (closing) return;
    if (outOfRange(d, min, max)) return;
    const iso = toISO(d);
    if (!isControlled) setInternal(iso);
    onChange?.(iso);
    setOpen(false);
  };

  const shiftMonth = (delta: number) => {
    if (closing) return;
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  // Build a 6×7 grid starting from the Sunday on or before the 1st of view month.
  const gridStart = React.useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return start;
  }, [view]);

  const days: Date[] = React.useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push(d);
    }
    return out;
  }, [gridStart]);

  const handleTriggerKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const base = enabledDate(currentDate ?? today, min, max);
      pendingFocusRef.current = toISO(base);
      setView({ year: base.getFullYear(), month: base.getMonth() });
      setOpen(true);
    } else if (open && e.key === "Tab" && !e.shiftKey) {
      const day =
        calRef.current?.querySelector<HTMLButtonElement>(
          '[data-calendar-date][aria-pressed="true"]:not([disabled])',
        ) ??
        calRef.current?.querySelector<HTMLButtonElement>(
          "[data-calendar-date]:not([disabled])",
        );
      if (day) {
        e.preventDefault();
        day.focus();
      }
    }
  };

  /* Attached to each (interactive) day button — arrows step the selection,
     Enter closes. preventDefault stops Enter from also "clicking" the button. */
  const handleGridKey = (e: React.KeyboardEvent) => {
    if (closing) return;
    const base =
      parseISO(
        e.currentTarget.getAttribute("data-calendar-date") ?? undefined,
      ) ??
      currentDate ??
      new Date(view.year, view.month, 1);
    let next: Date | undefined;
    if (e.key === "ArrowLeft")
      next = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 1);
    else if (e.key === "ArrowRight")
      next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 1);
    else if (e.key === "ArrowUp")
      next = new Date(base.getFullYear(), base.getMonth(), base.getDate() - 7);
    else if (e.key === "ArrowDown")
      next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 7);
    else if (e.key === "Home")
      next = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate() - base.getDay(),
      );
    else if (e.key === "End")
      next = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate() + 6 - base.getDay(),
      );
    if (next) {
      e.preventDefault();
      // Arrows must respect the same bounds as clicking, or the keyboard is a
      // way around them.
      if (outOfRange(next, min, max)) return;
      const iso = toISO(next);
      pendingFocusRef.current = iso;
      if (!isControlled) setInternal(iso);
      onChange?.(iso);
      setView({ year: next.getFullYear(), month: next.getMonth() });
    } else if (e.key === "Enter") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus?.();
    }
  };

  const trigger = (
    <button
      type="button"
      id={did}
      ref={triggerRef}
      disabled={disabled}
      onClick={() => !disabled && setOpen((v) => !v)}
      onKeyDown={handleTriggerKey}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      className={cn(
        dateTriggerClass({ size, invalid, disabled }),
        // The clear control is a SIBLING, never nested inside the trigger, so
        // it needs the trigger to give up its fixed w-full for the remaining
        // space in the row it now shares.
        clearable && "w-auto min-w-0 flex-1",
        className,
      )}
    >
      <span
        className={cn(
          "truncate",
          currentDate ? "text-pb-text" : "text-pb-text-muted",
        )}
      >
        {currentDate ? formatPretty(currentDate) : placeholder}
      </span>
      <Calendar className="w-3.5 h-3.5 text-pb-text-muted shrink-0" />
    </button>
  );

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={did}
          className="block text-xs font-medium text-pb-text-secondary mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative" ref={wrapRef}>
        {clearable ? (
          <div className="flex items-center gap-1.5">
            {trigger}
            {showClear && (
              <FieldClearButton clearLabel={clearLabel} onClear={handleClear} />
            )}
          </div>
        ) : (
          trigger
        )}
        {name && <input type="hidden" name={name} value={currentISO ?? ""} />}
      </div>
      {present &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={calRef}
            role="dialog"
            aria-label="Choose date"
            {...motionProps}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
            className={cn(
              "z-popover w-65 p-3",
              "rounded-md border border-pb-control-border bg-pb-card shadow-lg",
              motionProps.className,
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={() => shiftMonth(-1)}
                aria-label="Previous month"
                className={MONTH_NAV_CLASS}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <div className="text-xs font-medium text-pb-text">
                {MONTHS[view.month]} {view.year}
              </div>
              <button
                type="button"
                onClick={() => shiftMonth(1)}
                aria-label="Next month"
                className={MONTH_NAV_CLASS}
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className="text-dense-2 uppercase tracking-wider text-pb-text-muted text-center py-1"
                >
                  {w}
                </div>
              ))}
            </div>
            {/* Keyed on the visible month so paging REMOUNTS the grid: the
                whole set of days swaps at once, and `pb-motion-content-enter`
                has a fresh element to fade in rather than 42 labels flipping
                in place. */}
            <div
              key={`${view.year}-${view.month}`}
              className="grid grid-cols-7 gap-0.5 pb-motion-content-enter"
            >
              {days.map((d, i) => {
                const inMonth = d.getMonth() === view.month;
                const isToday = sameDay(d, today);
                const isSelected = currentDate
                  ? sameDay(d, currentDate)
                  : false;
                const blocked = outOfRange(d, min, max);
                return (
                  <button
                    key={i}
                    type="button"
                    data-calendar-date={toISO(d)}
                    onClick={() => commit(d)}
                    onKeyDown={handleGridKey}
                    aria-pressed={isSelected}
                    /* Disabled rather than hidden: a bounded month keeps its
                       shape, so the reader sees WHICH days are unavailable
                       instead of a calendar that looks partly broken. */
                    disabled={blocked}
                    aria-disabled={blocked || undefined}
                    className={cn(
                      "h-7 w-7 mx-auto rounded text-xs flex items-center justify-center transition-colors pb-tabular",
                      blocked
                        ? "text-pb-text-muted/40 cursor-not-allowed"
                        : isSelected
                          ? "bg-pb-teal text-pb-on-accent font-medium"
                          : inMonth
                            ? "text-pb-text hover:pb-overlay-paint"
                            : "text-pb-text-muted hover:pb-overlay-paint",
                      !blocked &&
                        !isSelected &&
                        isToday &&
                        "ring-1 ring-pb-teal/60",
                    )}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>,
          getLayerRoot(),
        )}
      {hasError ? (
        <p id={description} className="text-dense-1 mt-1 text-pb-red-text">
          {error}
        </p>
      ) : (
        hint && <p className="text-dense-1 text-pb-text-muted mt-1">{hint}</p>
      )}
    </div>
  );
}
